
export default function MarketTrendsSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            2026 Land Market Trends: What's Driving Demand
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            The land market is evolving. Here are the trends creating new opportunities for landowners in El Paso and beyond.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-teal-50 to-white rounded-lg p-8 shadow-lg border-2 border-teal-200">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-600 rounded-lg mb-6">
              <i className="ri-home-heart-line text-white text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Homesteading Movement</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The homesteading and self-sufficiency movement is booming. Thousands of families are leaving cities to build off-grid homes on rural land. They're looking for 1-10 acre parcels where they can grow food, raise animals, and live independently.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              This trend has increased demand for agricultural and rural land, especially properties with water rights, good soil, and southern exposure for solar power.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>What this means for sellers:</strong> Rural land that was hard to sell 10 years ago now has a growing buyer pool. Even remote properties have value to homesteaders.
            </p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg p-8 shadow-lg border-2 border-gray-200">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-900 rounded-lg mb-6">
              <i className="ri-home-smile-line text-white text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Tiny Home & ADU Demand</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Tiny homes and Accessory Dwelling Units (ADUs) are exploding in popularity. These compact dwellings require smaller lots (often 2,000-5,000 sq ft), creating demand for parcels that were previously considered "too small" to build on.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Many cities are relaxing zoning rules to allow ADUs and tiny homes, opening up new development possibilities for small lots.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>What this means for sellers:</strong> That "unbuildable" small lot might now be perfect for a tiny home or ADU. Zoning changes are creating value where none existed before.
            </p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-white rounded-lg p-8 shadow-lg border-2 border-orange-200">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-600 rounded-lg mb-6">
              <i className="ri-caravan-line text-white text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">RV & Mobile Home Living</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              With housing costs soaring, more people are choosing RV and mobile home living. They need land to park on - creating demand for rural parcels with minimal improvements.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              RV buyers don't need perfect land. They'll accept properties without utilities, rough access, or other issues that scare away traditional homebuyers.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>What this means for sellers:</strong> Raw land with basic access can appeal to RV owners looking for affordable places to live or vacation.
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-white rounded-lg p-8 shadow-lg border-2 border-green-200">
            <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-lg mb-6">
              <i className="ri-sun-line text-white text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Solar Farm Development</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Solar energy companies are leasing and buying large land parcels (20+ acres) for solar farms. El Paso's abundant sunshine makes it ideal for solar development.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Solar developers prefer flat, unobstructed land near power transmission lines. They'll pay premium prices for suitable properties.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>What this means for sellers:</strong> Large, flat parcels that seemed worthless for residential use might be valuable for solar development.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-12 text-white mb-16">
          <h3 className="text-3xl font-bold mb-6 text-center">Interest Rates & Economic Factors</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-xl font-bold mb-4 text-teal-400">The Challenge</h4>
              <p className="text-gray-300 leading-relaxed mb-4">
                Higher interest rates in 2024-2026 have made land financing more expensive and difficult to obtain. This has reduced the pool of financed buyers, causing some land prices to soften.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Traditional buyers who need loans are struggling to qualify or afford the higher payments, leading to longer selling times through conventional methods.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-4 text-teal-400">The Opportunity</h4>
              <p className="text-gray-300 leading-relaxed mb-4">
                Cash buyers like TRex RE LLC aren't affected by interest rates. We don't need bank financing, so we can make offers and close quickly regardless of economic conditions.
              </p>
              <p className="text-gray-300 leading-relaxed">
                In a high-rate environment, selling for cash becomes even more attractive. Why wait months for a financed buyer who might not qualify when you can close in 7 days with guaranteed cash?
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">El Paso's Growth Trajectory</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              El Paso continues to grow steadily, with population increases in East El Paso, Horizon City, and surrounding areas. This growth is pushing development further from the city center, creating value in previously rural areas.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Major infrastructure improvements - new roads, water lines, and commercial development - are extending into formerly remote areas. Land that was "too far out" five years ago is now in the path of growth.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              However, growth takes time. If you can't wait 5-10 years for your land to appreciate, selling now for cash might be your best option. We buy land in growth areas and hold it for long-term appreciation.
            </p>
            <a href="tel:+1-915-206-5814" className="inline-flex items-center space-x-2 px-6 py-3 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-phone-fill"></i>
              <span>Discuss Your Land's Potential</span>
            </a>
          </div>

          <div className="w-full h-96 rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/283fc89541ac5e97eae2c812b0da33f0.jpg"
              alt="El Paso land market trends 2026"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
